/**
 * Directory — full roster (active + former). Inline-editable for the
 * signed-in user's own row; admins can edit everyone's. Admin-only
 * Access column with promote / demote toggle.
 */
import { useState, useMemo, useEffect } from "preact/hooks";
import type { JSX } from "preact";
import { state } from "../state/store";
import { displayName, bySurname, nameToEmail } from "../utils/names";
import { restGet, restPatch, restUpsert } from "../utils/withRetry";
import { refreshProfilesByRoster } from "../state/realtime";
import { toast } from "../utils/toast";
import { ALLOWED_EMAIL_DOMAIN } from "../config";
import { RosterImport } from "./RosterImport";
import type { ProfileRow, RosterEntry } from "../types";

export function Directory(): JSX.Element {
  const s = state.value;
  const isAdmin = s.isAdmin;
  const isSuperAdmin = s.isSuperAdmin;
  const [search, setSearch] = useState("");
  const [importOpen, setImportOpen] = useState(false);
  // Manual "add a person" form (super admin only).
  const [addOpen, setAddOpen] = useState(false);
  const [addBusy, setAddBusy] = useState(false);
  const [nf, setNf] = useState(""); // new first name
  const [nl, setNl] = useState(""); // new last name
  const [nu, setNu] = useState(""); // new unit
  const [nr, setNr] = useState(""); // new role
  const [ne, setNe] = useState(""); // new email (optional → derived if blank)

  /** Add a brand-new active person to the roster (super admin only).
   *  Inserts via the same write path the Excel importer uses. */
  const addPerson = async (): Promise<void> => {
    if (!s.isSuperAdmin) return;
    const first = nf.trim().replace(/\s+/g, " ");
    const last = nl.trim().replace(/\s+/g, " ");
    if (!first || !last) {
      toast("First and last name are required.");
      return;
    }
    const name = `${first} ${last}`.replace(/\s+/g, " ").trim();
    if (state.value.roster[name]) {
      toast(`${name} is already in the roster.`);
      return;
    }
    const email = ne.trim().toLowerCase() || nameToEmail(name, ALLOWED_EMAIL_DOMAIN);
    if (!email.includes("@")) {
      toast("Enter a valid email address.");
      return;
    }
    const clash = Object.entries(state.value.roster).find(
      ([, e]) => (e.email ?? "").toLowerCase() === email,
    );
    if (clash) {
      toast(`That email is already used by ${displayName(clash[0])}.`);
      return;
    }
    const entry: RosterEntry = {
      unit: nu.trim(),
      role: nr.trim(),
      email,
      active: true,
      first_name: first,
      last_name: last,
      last_active_day: null,
    };
    setAddBusy(true);
    try {
      await restUpsert("roster", [{ name, ...entry }]);
    } catch (err) {
      toast(`Couldn't add — ${err instanceof Error ? err.message : "error"}.`);
      setAddBusy(false);
      return;
    }
    state.value = { ...state.value, roster: { ...state.value.roster, [name]: entry } };
    toast(`Added ${displayName(name)}`);
    setNf(""); setNl(""); setNu(""); setNr(""); setNe("");
    setAddOpen(false);
    setAddBusy(false);
  };

  /** Soft-remove / restore a person (super admin only). Never a hard
   *  delete: the calendar/notes/meetings FK is ON DELETE CASCADE, so a
   *  real delete would erase their attendance history. Mark-former keeps
   *  everything and just hides them from active views — and is reversible. */
  const setFormer = async (name: string, makeFormer: boolean): Promise<void> => {
    if (!s.isSuperAdmin) return;
    const entry = state.value.roster[name];
    if (!entry) return;
    if (makeFormer && !confirm(
      `Mark ${displayName(name)} as former?\n\nThey'll be hidden from active views and pickers. Their calendar history is kept, and you can reactivate them anytime.`,
    )) {
      return;
    }
    let lastDay = "";
    if (makeFormer) {
      const tap = (mp: Record<string, unknown> | undefined): void => {
        if (!mp) return;
        for (const d of Object.keys(mp)) if (d > lastDay) lastDay = d;
      };
      tap(state.value.calendar[name]);
      tap(state.value.notes[name]);
      tap(state.value.meetings[name]);
      if (!lastDay) lastDay = new Date().toISOString().slice(0, 10);
    }
    const body = makeFormer
      ? { active: false, last_active_day: lastDay }
      : { active: true, last_active_day: null };
    try {
      await restPatch("roster", `name=eq.${encodeURIComponent(name)}`, body);
    } catch (err) {
      toast(`Couldn't update — ${err instanceof Error ? err.message : "error"}.`);
      return;
    }
    const updated: RosterEntry = {
      ...entry,
      active: !makeFormer,
      last_active_day: makeFormer ? lastDay : null,
    };
    state.value = { ...state.value, roster: { ...state.value.roster, [name]: updated } };
    toast(makeFormer ? `${displayName(name)} marked as former` : `${displayName(name)} reactivated`);
  };

  /** 🔔 Choose which admins receive equipment-booking emails. Super admin
   *  only — the profiles guard trigger (migration 23) enforces it server-
   *  side too, so a plain admin can't flip it via the API either. */
  const toggleNotify = async (name: string): Promise<void> => {
    if (!s.isSuperAdmin) return;
    const p = s.profilesByRoster[name];
    if (!p) return;
    const next = !(p.notify_equipment ?? false);
    try {
      await restPatch("profiles", `id=eq.${encodeURIComponent(p.id)}`, { notify_equipment: next });
    } catch (err) {
      toast(`Couldn't update — ${err instanceof Error ? err.message : "error"}.`);
      return;
    }
    const updated = { ...state.value.profilesByRoster, [name]: { ...p, notify_equipment: next } };
    state.value = { ...state.value, profilesByRoster: updated };
    toast(
      next
        ? `${displayName(name)} will receive equipment emails`
        : `${displayName(name)} will no longer receive equipment emails`,
    );
  };

  // Re-sync profilesByRoster from the DB every time the Directory tab
  // opens. Heals any cache drift accumulated from missed realtime
  // broadcasts, optimistic UI updates that pre-date the b458c47 fix,
  // or simply tabs that have been open for hours. One round-trip per
  // open is cheap and prevents the "pill says Admin but DB doesn't"
  // class of bug entirely.
  useEffect(() => {
    if (isAdmin) void refreshProfilesByRoster();
  }, [isAdmin]);

  const allNames = useMemo(() => {
    return Object.keys(s.roster).sort((a, b) => {
      const aActive = s.roster[a]?.active !== false;
      const bActive = s.roster[b]?.active !== false;
      if (aActive !== bActive) return aActive ? -1 : 1;
      return bySurname(a, b);
    });
  }, [s.roster]);

  const filter = search.toLowerCase().trim();

  const updateField = (name: string, field: keyof RosterEntry, value: string): void => {
    const entry = s.roster[name];
    if (!entry) return;
    const trimmed = typeof value === "string" ? value.trim() : value;
    if (entry[field] === trimmed) return;  // no-op edit
    const updated = { ...entry, [field]: trimmed };
    state.value = {
      ...s,
      roster: { ...s.roster, [name]: updated },
    };
    // attachAutoSave fires asynchronously and pushes the diff to Supabase.
    // Surface a quick "Saved" toast so the admin can see the edit landed.
    toast(
      isAdmin && name !== s.currentUser
        ? `Saved — ${displayName(name)}`
        : "Saved",
    );
  };

  /** Cycle a teammate's role: User → Admin → User. Only super admins can
   *  do this; the trigger in migration 10 is the server-side guard. The
   *  super-admin tier itself isn't reachable from the UI on purpose —
   *  promote a super admin via direct SQL so the change can't slip
   *  through one mis-click.
   *
   *  Fetch-before-toggle: we ALWAYS re-read the target profile from the
   *  DB before deciding whether this is a promote or a demote, before
   *  the confirm dialog, and before the PATCH. The local
   *  profilesByRoster cache can be stale — a realtime broadcast might
   *  have been missed, an older optimistic update (pre-b458c47) might
   *  have left the cache out of sync with reality. The fresh fetch
   *  costs one round-trip but makes the toggle bulletproof and lets
   *  us silently heal the cache when we notice drift. */
  const toggleAdmin = async (name: string): Promise<void> => {
    if (!s.isSuperAdmin) {
      toast("Only super admins can change access roles.");
      return;
    }
    if (!s.profilesByRoster[name]) {
      toast(`${displayName(name)} doesn't have an account yet.`);
      return;
    }

    // Re-fetch the row from the DB — single source of truth.
    let live: ProfileRow;
    try {
      const rows = await restGet<ProfileRow>(
        "profiles",
        `roster_name=eq.${encodeURIComponent(name)}` +
          "&select=id,email,roster_name,is_admin,is_super_admin,password_set",
      );
      if (rows.length === 0) {
        toast(
          `${displayName(name)}'s account isn't in the database. ` +
            `They may have been removed — refresh the page to update the view.`,
        );
        return;
      }
      live = rows[0]!;
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Unknown error";
      toast(`Couldn't check current access — ${msg}.`);
      return;
    }

    // Silently heal the local cache if it disagreed with the DB. The
    // pill in the Directory will re-render before we even show the
    // confirm dialog, so the user sees the right starting state.
    const cached = s.profilesByRoster[name];
    if (
      cached &&
      (cached.is_admin !== live.is_admin ||
        cached.is_super_admin !== live.is_super_admin)
    ) {
      const updated = { ...s.profilesByRoster, [name]: live };
      state.value = { ...s, profilesByRoster: updated };
      console.warn(
        `Directory cache was stale for ${name} (cached.is_admin=${cached.is_admin}, db=${live.is_admin}). Healed.`,
      );
    }

    if (live.is_super_admin) {
      toast(
        `${displayName(name)} is a super admin — change that level via the Supabase dashboard.`,
      );
      return;
    }
    if (live.id === s.profileId) {
      toast("You can't change your own access — ask another super admin.");
      return;
    }

    const next = !live.is_admin;
    const verb = next ? "Promote" : "Demote";
    const action = next ? "grant admin access to" : "revoke admin access from";
    if (
      !confirm(
        `${verb} ${displayName(name)}?\n\nThis will ${action} ${displayName(name)}.\n\nNote: only the SUPER admin tier can manage roles. Plain admins see the dashboard but cannot promote or demote anyone.`,
      )
    ) {
      return;
    }

    try {
      await restPatch("profiles", `id=eq.${live.id}`, { is_admin: next }, 12000);
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Unknown error";
      toast(`Couldn't change access — ${msg}. Try again or refresh the page.`);
      return;
    }

    const finalProfile: ProfileRow = { ...live, is_admin: next };
    const updated = { ...state.value.profilesByRoster, [name]: finalProfile };
    state.value = { ...state.value, profilesByRoster: updated };
    toast(`${displayName(name)} is now ${next ? "an admin" : "a regular user"}`);
  };

  return (
    <div class="directory">
      <div class="cal-header">
        <h2>Team Directory</h2>
        <div class="filter-bar">
          <input
            type="text"
            class="cal-search"
            placeholder="Search…"
            onInput={(e) => setSearch((e.target as HTMLInputElement).value)}
          />
          {isSuperAdmin && (
            <>
              <button
                type="button"
                class="dir-add-btn"
                onClick={() => setAddOpen(true)}
                title="Add a new person to the roster manually"
              >
                ＋ Add person
              </button>
              <button
                type="button"
                class="dir-import-btn"
                onClick={() => setImportOpen(true)}
                title="Import the roster from an Excel file (e.g. the iCons HR file from SharePoint)"
              >
                📥 Import from Excel
              </button>
            </>
          )}
        </div>
      </div>
      {importOpen && <RosterImport onClose={() => setImportOpen(false)} />}
      {addOpen && isSuperAdmin && (
        <div class="modal-backdrop open" role="presentation" onClick={() => setAddOpen(false)}>
          <div
            class="modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="addPersonTitle"
            tabindex={-1}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 id="addPersonTitle">Add a person</h3>
            <div class="modal-intro">
              Adds a new active teammate to the roster. You can also import
              the whole list from the HR Excel file instead.
            </div>
            <div class="add-person-form">
              <label>First name
                <input value={nf} onInput={(e) => setNf((e.target as HTMLInputElement).value)} placeholder="e.g. Giulia" />
              </label>
              <label>Last name
                <input value={nl} onInput={(e) => setNl((e.target as HTMLInputElement).value)} placeholder="e.g. Rossi" />
              </label>
              <label>Unit
                <input value={nu} onInput={(e) => setNu((e.target as HTMLInputElement).value)} placeholder="e.g. Science Communication" />
              </label>
              <label>Role
                <input value={nr} onInput={(e) => setNr((e.target as HTMLInputElement).value)} placeholder="e.g. Officer" />
              </label>
              <label>Email <span class="add-person-opt">— optional, derived from the name if blank</span>
                <input type="email" value={ne} onInput={(e) => setNe((e.target as HTMLInputElement).value)} placeholder={`name.surname@${ALLOWED_EMAIL_DOMAIN}`} />
              </label>
            </div>
            <div class="modal-actions">
              <button type="button" class="modal-secondary" onClick={() => setAddOpen(false)}>Cancel</button>
              <button type="button" class="modal-primary" disabled={addBusy} onClick={() => void addPerson()}>
                {addBusy ? "Adding…" : "Add person"}
              </button>
            </div>
          </div>
        </div>
      )}
      <table class="dir">
        <thead>
          <tr>
            <th>Name</th>
            <th>Unit</th>
            <th>Role</th>
            <th>Email</th>
            {isAdmin && <th>Access</th>}
          </tr>
        </thead>
        <tbody>
          {allNames
            .filter((name) => {
              const info = s.roster[name];
              if (!info || !filter) return true;
              const hay = `${name} ${displayName(name)} ${info.unit} ${info.role} ${info.email}`.toLowerCase();
              return hay.includes(filter);
            })
            .map((name) => {
              const info = s.roster[name]!;
              const isFormer = info.active === false;
              const isMe = !isAdmin && name === s.currentUser;
              const dis = isFormer || (!isAdmin && !isMe);
              const profile = isAdmin ? s.profilesByRoster[name] : null;
              return (
                <tr key={name} class={[isMe ? "you-row" : "", isFormer ? "former-row" : ""].join(" ")}>
                  <td>
                    <b>{displayName(name)}</b>
                    {isMe && <span class="me-tag">YOU</span>}
                    {isFormer && <span class="former-tag">Former</span>}
                    {isSuperAdmin && (
                      <button
                        type="button"
                        class={`dir-row-action${isFormer ? " reactivate" : ""}`}
                        onClick={() => void setFormer(name, !isFormer)}
                        title={isFormer ? "Reactivate this person" : "Mark as former — kept in history, reversible"}
                      >
                        {isFormer ? "Reactivate" : "Mark former"}
                      </button>
                    )}
                  </td>
                  <td>
                    <input
                      value={info.unit}
                      disabled={dis}
                      onChange={(e) => updateField(name, "unit", (e.target as HTMLInputElement).value)}
                    />
                  </td>
                  <td>
                    <input
                      value={info.role}
                      disabled={dis}
                      onChange={(e) => updateField(name, "role", (e.target as HTMLInputElement).value)}
                    />
                  </td>
                  <td>
                    <input
                      value={info.email}
                      disabled={dis}
                      onChange={(e) => updateField(name, "email", (e.target as HTMLInputElement).value)}
                    />
                  </td>
                  {isAdmin && (
                    <td class="access-cell">
                      {(() => {
                        if (!profile) {
                          return (
                            <span
                              class="access-pill access-none"
                              title={`${displayName(name)} hasn't signed in yet — there's no account to promote until they do.`}
                            >
                              Not signed in
                            </span>
                          );
                        }
                        // Super admin pill: never editable from the UI.
                        if (profile.is_super_admin) {
                          return (
                            <span
                              class="access-pill access-super access-locked"
                              title="Super admin — change this level via the Supabase dashboard."
                            >
                              ⚡ Super admin
                            </span>
                          );
                        }
                        // Former employees: pill is read-only regardless of viewer.
                        if (isFormer) {
                          return (
                            <span
                              class={`access-pill access-${profile.is_admin ? "admin" : "user"} access-locked`}
                              title="Former employee — access cannot be changed."
                            >
                              {profile.is_admin ? "Admin (locked)" : "User (locked)"}
                            </span>
                          );
                        }
                        // Plain admin viewer: see the role but can't toggle it.
                        if (!s.isSuperAdmin) {
                          return (
                            <span
                              class={`access-pill access-${profile.is_admin ? "admin" : "user"} access-locked`}
                              title="Only super admins can change access roles."
                            >
                              {profile.is_admin ? "🛡 Admin" : "User"}
                            </span>
                          );
                        }
                        // Super admin viewer + own row: locked self-toggle.
                        if (profile.id === s.profileId) {
                          return (
                            <span
                              class={`access-pill access-${profile.is_admin ? "admin" : "user"} access-locked`}
                              title="You can't change your own access — ask another super admin."
                            >
                              {profile.is_admin ? "🛡 Admin" : "User"}
                            </span>
                          );
                        }
                        // Default: super admin viewer + a clickable other.
                        return (
                          <button
                            type="button"
                            class={`access-pill access-${profile.is_admin ? "admin" : "user"}`}
                            onClick={() => void toggleAdmin(name)}
                            title={`Click to ${profile.is_admin ? "demote to user" : "promote to admin"}`}
                          >
                            {profile.is_admin ? "🛡 Admin" : "User"}
                          </button>
                        );
                      })()}
                      {profile && profile.is_admin && !isFormer && (
                        s.isSuperAdmin ? (
                          <button
                            type="button"
                            class={`notify-pill${profile.notify_equipment ? " on" : ""}`}
                            aria-pressed={!!profile.notify_equipment}
                            onClick={() => void toggleNotify(name)}
                            title={
                              profile.notify_equipment
                                ? "Receives equipment-booking emails — click to stop"
                                : "Click to send this admin the equipment-booking emails"
                            }
                          >
                            {profile.notify_equipment ? "🔔" : "🔕"}
                          </button>
                        ) : profile.notify_equipment ? (
                          <span class="notify-pill on locked" title="Receives equipment-booking emails (chosen by a super admin)">
                            🔔
                          </span>
                        ) : null
                      )}
                    </td>
                  )}
                </tr>
              );
            })}
        </tbody>
      </table>
    </div>
  );
}
